import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/joy";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { List } from "../List";
import { AppContext } from "src/mtska-frontend-app-scaffolding";
import Input from "../InputElements/Input";

const hiddenFields = new Set(["id", "_id", "__v"]);

const normalizeRows = (value) => {
    if (Array.isArray(value)) {
        return value.map((row, index) => ({
            ...row,
            _id: row?._id ?? row?.id ?? `row-${index}`,
        }));
    }
    if (value && typeof value === "object") {
        return [
            {
                ...value,
                _id: value?._id ?? value?.id ?? "row-0",
            },
        ];
    }
    return [];
};

const buildFallbackFields = (rows) => {
    return Array.from(
        rows.reduce((acc, row) => {
            Object.keys(row || {}).forEach((key) => {
                if (!hiddenFields.has(key)) acc.add(key);
            });
            return acc;
        }, new Set())
    );
};

const getRouteTokenCandidates = (drawerRoute) => {
    if (!drawerRoute || typeof drawerRoute !== "string") return [];
    const tokens = drawerRoute
        .split("/")
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean);
    const baseToken = tokens[tokens.length - 1];
    if (!baseToken) return [];

    const candidates = new Set([baseToken]);
    if (baseToken.endsWith("ies") && baseToken.length > 3) {
        candidates.add(`${baseToken.slice(0, -3)}y`);
    }
    if (baseToken.endsWith("s") && baseToken.length > 1) {
        candidates.add(baseToken.slice(0, -1));
    } else {
        candidates.add(`${baseToken}s`);
        if (baseToken.endsWith("y") && baseToken.length > 1) {
            candidates.add(`${baseToken.slice(0, -1)}ies`);
        }
    }

    return Array.from(candidates);
};

const getTokenFromModulePath = (modulePath) => {
    if (!modulePath || typeof modulePath !== "string") return null;
    const tokens = modulePath
        .split("/")
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean);
    return tokens[tokens.length - 1] ?? null;
};

const normalizeTableColumn = (column) => {
    if (typeof column === "string") {
        return { kind: "data", field: column };
    }
    if (!column || typeof column !== "object") return null;
    return {
        kind: column.kind ?? "data",
        ...column,
    };
};

const getValueByPath = (obj, path) => {
    if (!path || typeof path !== "string") return undefined;
    return path
        .split(".")
        .filter(Boolean)
        .reduce((acc, token) => (acc == null ? undefined : acc[token]), obj);
};

const resolveControlValue = (row, control) => {
    if (control.binding) {
        const bound = getValueByPath(row, control.binding);
        if (typeof bound !== "undefined") return bound;
    }
    return row?.[control.field];
};

const getControlDefaultValue = (controlConfig, sourceValue) => {
    if (typeof sourceValue !== "undefined") return sourceValue;
    if (typeof controlConfig?.default !== "undefined") return controlConfig.default;
    if (controlConfig?.controlType === "switch") return false;
    return sourceValue;
};

const RelationshipTable = ({
    value,
    relation,
    name,
    setValue,
    editing,
    detailInspector = true,
    handleItemDetail,
    handleItemRemove,
    loadingDissociation,
}) => {
    const { t } = useTranslation();
    const { existingModules } = useContext(AppContext);
    const apiRef = useGridApiRef();
    const rows = useMemo(() => normalizeRows(value), [value]);
    const selectionControl = relation?.selectionControl ?? null;
    const checkboxSelectionEnabled = Boolean(editing && selectionControl === "checkbox");
    const [selectionModel, setSelectionModel] = useState(() => rows.map((row) => row._id));
    const [controlValues, setControlValues] = useState({});
    const controlValuesRef = useRef(controlValues);
    controlValuesRef.current = controlValues;
    const prevPayloadRef = useRef(null);

    const configBasedColumns = useMemo(() => {
        const configColumns = Array.isArray(relation?.tableColumns) ? relation.tableColumns : null;
        if (!configColumns?.length) return null;
        return configColumns.map(normalizeTableColumn).filter(Boolean);
    }, [relation?.tableColumns]);

    const fallbackColumns = useMemo(
        () => buildFallbackFields(rows).map(normalizeTableColumn).filter(Boolean),
        [rows]
    );

    const normalizedTableColumns = configBasedColumns ?? fallbackColumns;

    const pinnedColumns = useMemo(() => {
        const left = [];
        const right = [];
        normalizedTableColumns.forEach((col) => {
            if (col.pinned === "left") left.push(col.field);
            else if (col.pinned === "right") right.push(col.field);
        });
        if (!left.length && !right.length) return undefined;
        return { left, right };
    }, [normalizedTableColumns]);

    const controlColumns = useMemo(
        () =>
            normalizedTableColumns.filter(
                (column) => (column?.kind ?? "data") === "control" && typeof column?.field === "string"
            ),
        [normalizedTableColumns]
    );

    useEffect(() => {
        setSelectionModel(rows.map((row) => row._id));
    }, [rows]);

    useEffect(() => {
        if (!editing || !name || typeof setValue !== "function") return;
        const selectedIds = Array.isArray(selectionModel) ? selectionModel : [];
        const selectedSet = new Set(selectedIds);
        const effectiveRows = checkboxSelectionEnabled
            ? rows.filter((row) => selectedSet.has(row._id))
            : rows;

        if (!controlColumns.length) {
            const simplePayload = checkboxSelectionEnabled ? selectedIds : effectiveRows.map((row) => row._id);
            const simpleJson = JSON.stringify(simplePayload);
            if (prevPayloadRef.current === simpleJson) return;
            prevPayloadRef.current = simpleJson;
            setValue(name, simplePayload);
            return;
        }

        const payload = effectiveRows.map((row) => {
            const rowControls = controlValues[row._id] || {};
            const nextItem = { _id: row._id };

            controlColumns.forEach((control) => {
                const sourceValue = resolveControlValue(row, control);
                let valueToSave = rowControls[control.field];

                if (typeof valueToSave === "undefined") {
                    valueToSave = getControlDefaultValue(control, sourceValue);
                }

                nextItem[control.field] = valueToSave;
            });

            return nextItem;
        });

        const payloadJson = JSON.stringify(payload);
        if (prevPayloadRef.current === payloadJson) return;
        prevPayloadRef.current = payloadJson;

        setValue(name, payload);
    }, [selectionModel, setValue, name, editing, rows, controlColumns, controlValues, checkboxSelectionEnabled]);

    useEffect(() => {
        setControlValues((prev) => {
            const next = {};
            let changed = false;

            rows.forEach((row) => {
                if (prev[row._id]) {
                    next[row._id] = prev[row._id];
                } else {
                    const rowValues = {};
                    controlColumns.forEach((control) => {
                        const sourceValue = resolveControlValue(row, control);
                        rowValues[control.field] = getControlDefaultValue(control, sourceValue);
                    });
                    next[row._id] = rowValues;
                    changed = true;
                }
            });

            if (!changed && Object.keys(prev).length === Object.keys(next).length) {
                return prev;
            }
            return next;
        });
    }, [rows, controlColumns]);

    const resolvedDataProviderName = useMemo(() => {
        if (relation?.dataProviderName) return relation.dataProviderName;
        const routeCandidates = getRouteTokenCandidates(relation?.drawerRoute);
        if (!routeCandidates.length) return null;
        if (!existingModules || typeof existingModules !== "object") return null;

        const entries = Object.entries(existingModules).filter(([, config]) => config && typeof config === "object");

        // Exact module-key token match has the highest confidence.
        for (const routeToken of routeCandidates) {
            const byModuleToken = entries.find(([moduleName]) => getTokenFromModulePath(moduleName) === routeToken);
            if (byModuleToken) return byModuleToken[0];
        }

        // Fallback to module modelName token match.
        for (const routeToken of routeCandidates) {
            const byModelName = entries.find(([, config]) => {
                const modelToken = getTokenFromModulePath(config?.modelName);
                return modelToken === routeToken;
            });
            if (byModelName) return byModelName[0];
        }

        return null;
    }, [relation?.dataProviderName, relation?.drawerRoute, existingModules]);

    const updateControlValue = (rowId, field, value) => {
        setControlValues((prev) => ({
            ...prev,
            [rowId]: {
                ...(prev[rowId] || {}),
                [field]: value,
            },
        }));
    };

    const tableColumns = useMemo(() => {
        return normalizedTableColumns
            .map((column) => {
                if (!column?.field) return null;

                if ((column.kind ?? "data") === "control") {
                    return {
                        field: column.field,
                        headerName: column.headerName ?? column.field,
                        width: column.width ?? 120,
                        minWidth: column.minWidth ?? 90,
                        maxWidth: column.maxWidth,
                        align: "center",
                        headerAlign: "center",
                        sortable: false,
                        filterable: false,
                        hideable: false,
                        disableColumnMenu: true,
                        renderCell: (params) => (
                            <Box
                                onClick={(e) => {
                                    if (editing) e.stopPropagation();
                                    if (editing && e.target?.tagName !== 'INPUT' && column.controlType === 'switch') {
                                        const input = e.currentTarget.querySelector('input[type="checkbox"]');
                                        if (input) {
                                            input.click();
                                        } else {
                                            const cur = controlValuesRef.current?.[params.row._id]?.[column.field];
                                            updateControlValue(params.row._id, column.field, !cur);
                                        }
                                    }
                                }}
                                onMouseDown={(e) => { if (editing) e.stopPropagation(); }}
                                sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                                <Input
                                    type={column.controlType}
                                    name={column.field}
                                    values={column.values}
                                    options={column.options}
                                    value={controlValuesRef.current?.[params.row._id]?.[column.field]}
                                    editing={editing}
                                    onChange={(nextValue) => {
                                        const resolved = nextValue?.target?.value ?? nextValue;
                                        updateControlValue(params.row._id, column.field, resolved);
                                    }}
                                />
                            </Box>
                        ),
                    };
                }

                return {
                    field: column.field,
                    headerName: column.headerName ?? column.field,
                    width: column.width,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                };
            })
            .filter(Boolean);
    }, [normalizedTableColumns, editing]);

    if (!rows.length) {
        return (
            <Box className="relationship-elements relationship-elements-relationshiptable">
                {t("none")}
            </Box>
        );
    }


    return (
        <Box className="relationship-elements relationship-elements-relationshiptable">
            
            <List
                apiRef={apiRef}
                dataProviderName={resolvedDataProviderName}
                items={rows}
                total={rows.length}
                loading={false}
                columnsNeeded={[]}
                extraColumns={tableColumns}
                pinnedColumns={pinnedColumns}
                hideTableToolbar
                hideTableFooter
                headerFilters={false}
                disableColumnFilter
                disableColumnSort
                disableColumnMenu={true}
                autoHeight={true}
                isEmbedded
                className="relationship-elements relationship-elements-relationshiptable-list"
                onFilterPageSortChange={() => {}}
                
                enableSelection={checkboxSelectionEnabled}
                disableMultipleRowSelection={false}
                rowSelectionExternalListener={checkboxSelectionEnabled ? setSelectionModel : undefined}
                selectionModel={checkboxSelectionEnabled ? selectionModel : undefined}
            />
        </Box>
    );
};

export default RelationshipTable;
